import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CommonState } from '../store/reducer';
import { CurrentState } from '../store/current-state';
import { ServicesCommon } from './services-common.service';
import { Image, EquipmentDto } from '../model';
import { ENVIRONMENT_TOKEN } from '../../environment/token-variables';
import {CameraImage, QuickLookImage} from '../model/ImageDatas';
import { DateFormatterService } from './date-formatter.service';

@Injectable()
export class ImagesService {
  private imageLoadWorkers = [];
  private processingCnt = [];

  constructor(
    private currentState: CurrentState<CommonState>,
    protected http: HttpClient,
    @Inject(ENVIRONMENT_TOKEN) private readonly environment,
    private servicesCommon: ServicesCommon,
    private dateFormatterService: DateFormatterService) {
  }

  getMultipleCameraImages(equipmentIds: number[], date: string) {
    const cameraList = equipmentIds.join(',');
    if (date) {
      return this.servicesCommon.getAll<Image[]>(
        `/private/assetmonitor/equipment/camera/images?date=${date}&ids=${cameraList}`);
    }
  }


  getCameraImages(equipmentId: number, date: string) {
    if (date) {
      return this.servicesCommon.getAll<Image>(
        `/private/assetmonitor/equipment/camera/${equipmentId}/images?date=${date}`);
    }
    return this.servicesCommon.getAll<Image>(
      `/private/assetmonitor/equipment/camera/${equipmentId}/images`);
  }

  getUserCameras(userId, loadSharedCameras = true) {
    const url = this.servicesCommon.getUrl(`/private/assetmonitor/user/${userId}/equipment/camera`);
    let params = new HttpParams();
    if (!loadSharedCameras) {
      params = params.append('loadSharedCameras', "false");
    } else {
      params = params.append('loadSharedCameras', "true");
    }
    const options = {
      headers: this.servicesCommon.getAuthHeaders(),
      params: params
    };
    return this.http.get<EquipmentDto[]>(url, options);
  }

  getUserQuickLook(userId) {
    return this.servicesCommon.getAll<Image>(`/private/assetmonitor/quicklook/user/${userId}`);
  }

  cameraSort = (a: EquipmentDto, b: EquipmentDto) => a.cameraTitle > b.cameraTitle ? 1 : -1;

  mapQuickLookCameras(c: EquipmentDto[], images: Image[]): QuickLookImage[] {
    let cameras = [].concat(c);
    const sorted = cameras.sort(this.cameraSort).map((camera: EquipmentDto) => {
      const image = images.find(img => {
        if (!!img.equipmentId) {
          return img.equipmentId === camera.id
        }
        return img.cameraId == camera.cameraId + '';
      });
      let mostRecentImage = null;
      let displayDate = '';
      if (image) {
        if (image.imageName) {
          mostRecentImage = {
            id: image.imageId,
            downloadUrl: this.environment.backend + `/private/assetmonitor/images/file/${image.imageName}`,
            path: `camera-image-${image.imageId}.jpg`,
          };
          displayDate = this.dateFormatterService.fullDate(this.dateFormatterService.fromDateObj(image.captureDate));
        } else {
          console.error('Quicklook images - missing name property');
        }

      }
      return {
        imageCamera: camera,
        image: {
          id: camera.cameraId,
          equipmentId: camera.id,
          title: camera.cameraTitle,
          mostRecentImage,
          images: []
        },
        displayDate
      };
    });
    return sorted;
  }

  minIndex() {
    return this.processingCnt.reduce((minIdx, val, idx) => val < this.processingCnt[minIdx] ? idx : minIdx, 0);
  }

  async loadImage(image, thumbnail = false) {
    if (image) {
      const token = this.currentState.snapshot.user.token;
      const minIdx = this.minIndex();
      this.processingCnt[minIdx]++;
      this.imageLoadWorkers[minIdx]
        .postMessage({ type: 'LOAD_ASSET_IMAGE', downloadUrl: image.downloadUrl, thumbnail, id: image.id, token });
    }
  }

  mapImageCameras(cameras: EquipmentDto[], images: Image[], date: string, withFullDate = false): CameraImage[] {
    return cameras.map((camera: EquipmentDto) => {
      const cameraImages = images
        .sort(this.imageDateSort)
        .filter(img => {
          if (img.equipmentId) {
            return camera.id === img.equipmentId
          }
          return img.cameraId == camera.cameraId + '';
        })
        .map(img => ({
          id: img.imageId,
          downloadUrl: this.environment.backend + `/private/assetmonitor/images/file/${img.imageName}`,
          path: `camera-image-${img.imageId}.jpg`,
          displayDate: img.captureDate ?
            (withFullDate ?
                this.dateFormatterService.long(this.dateFormatterService.fromDateObj(img.captureDate)) :
                this.dateFormatterService.time(this.dateFormatterService.fromDateObj(img.captureDate))
            ) : ''
        }));
      const image = {
        id: camera.cameraId,
        equipmentId: camera.id,
        title: camera.cameraTitle,
        mostRecentImage: cameraImages.length > 0 ? cameraImages[cameraImages.length - 1] : null,
        images: cameraImages
      };
      if (!!date) {
        return {
          imageCamera: camera,
          image,
          displayDate: date
        };
      } else {
        return {
          imageCamera: camera,
          image
        };
      }

    });
  }

  imageDateSort = (a: Image, b: Image) => {
    if (a.captureDate && b.captureDate) {
      const dateA = this.dateFormatterService.fromDateObj(a.captureDate);
      const dateB = this.dateFormatterService.fromDateObj(b.captureDate);
      return (dateA < dateB) ? 1 : ((dateA > dateB) ? -1 : 0);
    }
    return 0;
  }
}


